import React, { FC, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Column, DataTable } from "@/components/ui/data-table";
import { Switch } from "@/components/ui/switch";
import { Edit, Loader2, LogIn, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Role } from "@/types/user";
import { getUserDisplayRoleLabel, getUsersGroupHeader } from "./usersLabels";
import { UserProfile } from "@/types/user-profile";
import { PresignedImageWithPlaceholder } from "@/components/common/PresignedImageWithPlaceholder";
import { Checkbox } from "@/components/ui/checkbox";
import type { CheckboxSelectionResult } from "@/hooks/useCheckboxSelection";

interface Props {
  handleSort: (value: string) => void;
  setCurrentPage: (page: number) => void;
  sortField: string | null;
  sortDirection: "asc" | "desc" | null;
  isLoading: boolean;
  currentPage: number;
  totalPages: number;
  users: UserProfile[];
  canOpenUserDetails: boolean;
  canViewUserDetails: boolean;
  canImpersonateUsers: boolean;
  currentUserId?: string;
  isStartingImpersonation: boolean;
  impersonatingUserId: string | null;
  onImpersonateUser: (userId: string) => Promise<void>;
  canEditGuesteriaAdmin: boolean;
  canDeleteUser: boolean;
  onDeleteUser: (user: UserProfile) => void;
  isDeletingUser: boolean;
  showTenantColumn: boolean;
  onToggleUserActive?: (userId: string, isActive: boolean) => void;
  staffStatusMap?: Map<string, "on-site" | "not-on-site">;
  selection: CheckboxSelectionResult;
}

export const UsersTable: FC<Props> = ({
  handleSort,
  users,
  isLoading,
  sortDirection,
  sortField,
  totalPages,
  currentPage,
  setCurrentPage,
  canOpenUserDetails,
  canViewUserDetails,
  canImpersonateUsers,
  currentUserId,
  isStartingImpersonation,
  impersonatingUserId,
  onImpersonateUser,
  canEditGuesteriaAdmin,
  canDeleteUser,
  onDeleteUser,
  isDeletingUser,
  showTenantColumn,
  onToggleUserActive,
  staffStatusMap,
  selection,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    selectAll,
    toggleSelectAll,
    toggleSelectItem: toggleSelectUser,
    isSelected,
  } = selection;

  const cellTextClassName =
    "text-zinc-900 text-base font-normal tracking-tight";
  const rowCellClassName = "py-0";
  const translate = (key: string, defaultValue?: string) =>
    t(key, defaultValue ?? key);
  const getDisplayRoleLabel = (row: UserProfile): string =>
    getUserDisplayRoleLabel(translate, row);
  const getGroupNames = (row: UserProfile): string[] => {
    if (row.accessGroups && row.accessGroups.length > 0) {
      return row.accessGroups.map((group) => group.name);
    }
    const teams = row.staff?.userTeams;
    if (teams && teams.length > 0) {
      return teams.map((ut) => ut.team.name);
    }
    return [];
  };
  const getUserDetailPath = (row: UserProfile): string =>
    row.role === "GUESTERIA_ADMIN"
      ? `/users/guesteria-admins/${row.id}/edit`
      : `/users/${row.id}`;

  const canImpersonateTarget = (row: UserProfile): boolean =>
    canImpersonateUsers &&
    row.id !== currentUserId &&
    row.role !== Role.TENANT_ADMIN &&
    row.role !== "SUPER_ADMIN";

  const canToggleStatusTarget = (row: UserProfile): boolean =>
    !!onToggleUserActive &&
    row.id !== currentUserId &&
    row.role !== "SUPER_ADMIN" &&
    // Native TENANT_ADMIN accounts are protected from peer deactivation, but
    // custom roles built on the TENANT_ADMIN base (which still carry `role:
    // TENANT_ADMIN` for permission inheritance) are a distinct, admin-managed
    // grant and can be toggled.
    (row.role !== Role.TENANT_ADMIN || (row.customRoles?.length ?? 0) > 0);

  const getToggleTitle = (isActive: boolean): string =>
    isActive ? t("users.table.deactivateUser") : t("users.table.activateUser");

  const getDeleteTitle = (row: UserProfile, canDelete: boolean): string => {
    if (canDelete) {
      return t("users.table.deleteUser");
    }
    if (row.id === currentUserId) {
      return t("users.table.cannotDeleteCurrentUser");
    }
    return t("common.insufficientPermissions", "Insufficient permissions");
  };

  const columns: Column<UserProfile>[] = [
    {
      key: "select",
      header: (
        <Checkbox
          className="size-5 border-zinc-900"
          checked={selectAll}
          onCheckedChange={toggleSelectAll}
          aria-label={t("users.table.selectAll")}
          disabled={users.length === 0}
        />
      ),
      className: "w-10 text-center",
      render: (_, row) => (
        <Checkbox
          className="size-5 border-zinc-900"
          checked={isSelected(row.id)}
          onCheckedChange={() => toggleSelectUser(row.id)}
          onClick={(event) => event.stopPropagation()}
          aria-label={t("users.table.selectUser", {
            name:
              `${row.firstName ?? ""} ${row.lastName ?? ""}`.trim() ||
              row.email,
          })}
        />
      ),
    },
    {
      key: "status",
      header: t("users.table.status"),
      className: `text-center ${rowCellClassName}`,
      sortable: true,
      render: (_, row) => {
        const isStaff = row.role === Role.STAFF;
        const staffIsActive = row.staff?.isActive;
        const effectivelyActive =
          isStaff && staffIsActive != null
            ? staffIsActive
            : row.status !== "INACTIVE";
        const canToggle = canToggleStatusTarget(row);
        return (
          <div
            className="flex items-center justify-center"
            onClick={(event) => {
              if (canToggle) {
                event.stopPropagation();
              }
            }}
            title={canToggle ? getToggleTitle(effectivelyActive) : undefined}
          >
            <Switch
              checked={effectivelyActive}
              disabled={!canToggle}
              className={!canToggle ? "pointer-events-none" : undefined}
              onCheckedChange={(isActive) =>
                onToggleUserActive?.(row.id, isActive)
              }
            />
          </div>
        );
      },
    },
    {
      key: "name",
      header: (
        <span className="block text-center">{t("users.table.name")}</span>
      ),
      sortable: true,
      className: `text-center ${rowCellClassName}`,
      render: (_, row) => {
        const staffStatus =
          (row.staff?.id ? staffStatusMap?.get(row.staff.id) : undefined) ??
          staffStatusMap?.get(row.id);
        const isOnSite = staffStatus === "on-site";
        return (
          <div
            className={
              !canViewUserDetails
                ? "flex min-h-[72px] items-center gap-2 py-2"
                : "flex min-h-[72px] cursor-pointer items-center gap-2 py-2 hover:opacity-80"
            }
            onClick={
              canViewUserDetails
                ? () => navigate(getUserDetailPath(row))
                : undefined
            }
          >
            <div className="relative shrink-0">
              <PresignedImageWithPlaceholder
                className={"h-8 w-8 rounded-full object-cover"}
                photo={row.photoId}
                placeholder={
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-200">
                    <span className="text-sm font-medium text-gray-600">
                      {row.firstName?.charAt(0)}
                      {row.lastName?.charAt(0)}
                    </span>
                  </div>
                }
                alt={`${row.firstName} ${row.lastName}`}
              />
              {isOnSite && (
                <span className="border-background absolute right-0 bottom-0 size-2.5 rounded-full border-2 bg-green-500" />
              )}
            </div>
            <div className="flex h-8 w-fit flex-col items-start justify-center gap-1">
              <p className="text-foreground text-[10px] leading-[10px] font-semibold tracking-normal whitespace-nowrap">
                {row.firstName} {row.lastName}
              </p>
              <p className="text-muted-foreground text-[10px] leading-[10px] font-normal tracking-normal whitespace-nowrap">
                {row.staff?.position}
              </p>
            </div>
          </div>
        );
      },
    },
    ...(showTenantColumn
      ? [
          {
            key: "tenant.name",
            header: t("users.table.tenant"),
            render: (_value: unknown, row: UserProfile) => {
              if (!row.tenant) {
                return <span className={cellTextClassName}>—</span>;
              }

              return (
                <div className="flex flex-col">
                  <span className={cellTextClassName}>{row.tenant.name}</span>
                  <span className="text-muted-foreground text-sm">
                    {row.tenant.slug}
                  </span>
                </div>
              );
            },
            className: `text-center ${rowCellClassName}`,
          },
        ]
      : []),
    {
      key: "email",
      header: t("users.table.email"),
      sortable: true,
      render: (value) => {
        return (
          <span className="font-sans text-[10px] leading-4 font-medium tracking-normal text-neutral-500">
            {value}
          </span>
        );
      },
      className: `text-center ${rowCellClassName}`,
    },
    {
      key: "phone",
      header: t("users.table.phoneNumber"),
      render: (value) => {
        return (
          <span className="font-sans text-[10px] leading-4 font-medium tracking-normal text-neutral-500">
            {value}
          </span>
        );
      },
      sortable: true,
      className: `text-center ${rowCellClassName}`,
    },
    {
      key: "role",
      header: t("users.table.role"),
      render: (_value, row) => {
        return (
          <span className="font-sans text-[10px] leading-4 font-medium tracking-normal text-neutral-500">
            {getDisplayRoleLabel(row)}
          </span>
        );
      },
      sortable: true,
      className: `text-center ${rowCellClassName}`,
    },
    {
      key: "group",
      header: getUsersGroupHeader(translate),
      render: (_value, row) => {
        const groupNames = getGroupNames(row);
        if (groupNames.length === 0) {
          return (
            <span className="font-sans text-[10px] leading-4 font-medium tracking-normal text-neutral-500">
              —
            </span>
          );
        }
        const maxVisible = 3;
        const hasOverflow = groupNames.length > maxVisible;
        const visibleNames = hasOverflow
          ? groupNames.slice(0, maxVisible - 1)
          : groupNames;
        const hiddenCount = groupNames.length - visibleNames.length;
        return (
          <div
            className="flex flex-col items-center gap-0.5"
            title={groupNames.join(", ")}
          >
            {visibleNames.map((name) => (
              <span
                key={name}
                className="max-w-full truncate font-sans text-[10px] leading-4 font-medium tracking-normal text-neutral-500"
              >
                {name}
              </span>
            ))}
            {hiddenCount > 0 && (
              <span className="font-sans text-[10px] leading-4 font-medium tracking-normal text-neutral-400">
                +{hiddenCount}
              </span>
            )}
          </div>
        );
      },
      className: `text-center ${rowCellClassName}`,
      sortable: true,
    },
    {
      key: "actions",
      header: t("users.table.actions"),
      className: `text-end ${rowCellClassName}`,
      render: (_, row) => {
        const isGuesteriaAdmin = row.role === "GUESTERIA_ADMIN";
        const canImpersonate = canImpersonateTarget(row);
        const isCurrentUserImpersonationPending =
          isStartingImpersonation && impersonatingUserId === row.id;
        const canEdit = isGuesteriaAdmin
          ? canEditGuesteriaAdmin
          : canOpenUserDetails;
        const canDelete = canDeleteUser && row.id !== currentUserId;
        const editPath = isGuesteriaAdmin
          ? `/users/guesteria-admins/${row.id}/edit`
          : `/users/${row.id}?edit=true`;

        return (
          <div className="flex items-center justify-end gap-2">
            {canImpersonate ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={(event) => {
                  event.stopPropagation();
                  void onImpersonateUser(row.id);
                }}
                title={t("users.table.loginAsUser")}
                aria-label={t("users.table.loginAsUser")}
                disabled={isStartingImpersonation}
              >
                {isCurrentUserImpersonationPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <LogIn className="h-4 w-4" />
                )}
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={(event) => {
                event.stopPropagation();
                navigate(editPath);
              }}
              title={
                canEdit
                  ? t("users.table.editUser")
                  : t(
                      "common.insufficientPermissions",
                      "Insufficient permissions",
                    )
              }
              disabled={!canEdit}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={(event) => {
                event.stopPropagation();
                onDeleteUser(row);
              }}
              title={getDeleteTitle(row, canDelete)}
              aria-label={t("users.table.deleteUser")}
              disabled={!canDelete || isDeletingUser}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    },
  ];

  const onDataTableRowClick = useCallback(
    (row: UserProfile) => navigate(getUserDetailPath(row)),
    [navigate],
  );

  return (
    <DataTable
      data={users}
      columns={columns}
      loading={isLoading}
      columnHeaderClassName="inline-flex h-10 items-center gap-1 px-2 font-sans text-[10px] font-medium leading-4 tracking-normal text-neutral-500"
      onRowClick={canViewUserDetails ? onDataTableRowClick : undefined}
      pagination={{
        currentPage,
        totalPages,
        onPageChange: setCurrentPage,
      }}
      sorting={{
        field: sortField,
        direction: sortDirection,
        onSort: handleSort,
      }}
      emptyMessage={t("users.table.noUsersFound")}
    />
  );
};
